import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { observer } from 'mobx-react';
import { useStores } from '../../hooks/use-stores';
import EventIcon from '@material-ui/icons/Event';
import ScheduleIcon from '@material-ui/icons/NotificationsActive';
import { FormControlLabel, makeStyles, Tooltip } from '@material-ui/core';
import moment from 'moment/moment';
import Checkbox from '@material-ui/core/Checkbox';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Errormessage from '../../components/errormessage';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import ErrorMessage from '../../components/errormessage';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import IconButton from '@material-ui/core/IconButton';
import MenuItem from '@material-ui/core/MenuItem';
import Edit from '@material-ui/icons/Edit';
import _ from 'lodash';
import { formatFrequency, formatWeekday } from '../../utils/helpers';

const SCHEDULE_INTERVALS = [
    { value: 'W', label: 'Weekly' },
    { value: 'B', label: 'Biweekly' },
    { value: 'M', label: 'Monthly' },
];

const HOURS_0_23 = Array.from({ length: 24 }, (_, h) => h);

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    textField: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
        width: 200,
    },
}));

const formatScheduleType = (type) => {
    switch (Number(type)) {
        case 0:
            return 'Now';
        case 1:
            return 'Scheduled';
        case 2:
            return 'One by one';
        case 3:
            return 'Download links';
        default:
            return `${type ?? '—'}`;
    }
};

const stripHtml = (value) =>
    (value || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const decodeReminderChannel = (channel) => {
    switch (Number(channel)) {
        case 1:
            return 'Email';
        case 2:
            return 'Slack';
        case 3:
            return 'MS Teams';
        default:
            return 'Unknown';
    }
};

const Row = observer(({ data, index, trainingID, subjects }) => {
    const classes = useStyles();
    const { commonStore, scheduleStore } = useStores();

    const { cleaningNotifications } = scheduleStore;

    const handleStatusChange = (data) => {
        scheduleStore.updateEvent(index, {
            dirty: true,
            status: data.status === 2 ? 0 : 2,
        });
    };

    const handleSendChange = (e) => {
        scheduleStore.updateEvent(index, { dirty: true, send: e.target.value });
    };

    const revertChanges = () => {
        scheduleStore.revertEvent(index);
    };

    const saveChanges = () => {
        scheduleStore.saveChanges(index);
    };

    const cleanNotifications = () => {
        commonStore.showConfirm(
            <>
                Are you sure you want to clean all notifications attached to
                this event?
                <br />
                <strong>
                    THINK TWICE ON PRODUCTION ENV. This will cause resend
                    notifications to all recipients.
                </strong>
            </>,
            'Clean',
            'Clean notifications',
            async () => {
                const result = await scheduleStore.cleanNotifications(index);
                if (result) {
                    commonStore.showMessage('Notifications cleaned');
                    scheduleStore.loadSchedule(trainingID);
                }
            }
        );
    };

    return (
        <tr>
            <td>{data.id}</td>
            <td>{data.type === 2 ? <ScheduleIcon /> : <EventIcon />}</td>
            <td>
                <Tooltip
                    title={
                        <div>
                            {subjects.map((s) => (
                                <div>{s.name}</div>
                            ))}
                        </div>
                    }
                >
                    <span>{subjects.map((s) => s.subject_id).join(', ')}</span>
                </Tooltip>
            </td>

            <td>
                <Checkbox
                    checked={data.status === 2}
                    onChange={() => {
                        handleStatusChange(data);
                    }}
                    inputProps={{ 'aria-label': 'primary checkbox' }}
                />
            </td>
            <td>
                <TextField
                    id="datetime-local"
                    label="Send date/time"
                    type="datetime-local"
                    value={moment(data.send).format('YYYY-MM-DDTHH:MM')}
                    className={classes.textField}
                    onChange={handleSendChange}
                    InputLabelProps={{
                        shrink: true,
                    }}
                />
            </td>
            <td>
                {data.dirty ? (
                    <Button
                        variant={'outlined'}
                        size={'small'}
                        color={'primary'}
                        onClick={saveChanges}
                    >
                        Save
                    </Button>
                ) : null}
                {data.dirty ? (
                    <Button
                        variant={'outlined'}
                        size={'small'}
                        color={'secondary'}
                        onClick={revertChanges}
                    >
                        Revert
                    </Button>
                ) : null}
            </td>
            <td>
                {data.notificationsCount > 0 && (
                    <>
                        {' '}
                        {data.notificationsCount} attached notifications.{' '}
                        {cleaningNotifications.includes(data.id) ? (
                            <CircularProgress />
                        ) : (
                            <Button
                                variant={'outlined'}
                                size={'small'}
                                color={'secondary'}
                                onClick={cleanNotifications}
                            >
                                Clean
                            </Button>
                        )}
                    </>
                )}
            </td>
        </tr>
    );
});

function Schedule() {
    const { trainingId: routeTrainingId } = useParams();
    const navigate = useNavigate();
    const [trainingID, setTrainingID] = useState(routeTrainingId || '');
    const [completeEmail, setCompleteEmail] = useState('');
    const { scheduleStore, superStore, commonStore } = useStores();
    const [standards, setStandards] = React.useState([]);
    const [rrule, setRRule] = React.useState('');
    const [listsDialogOpen, setListsDialogOpen] = useState(false);
    const [listsSelection, setListsSelection] = useState({});
    const [editingSchedule, setEditingSchedule] = useState(false);
    const [editSubjectsPer, setEditSubjectsPer] = useState(1);
    const [editInterval, setEditInterval] = useState('W');
    const [editWeekday, setEditWeekday] = useState(1);
    const [editHour, setEditHour] = useState(9);
    const {
        loadingSchedule,
        schedule,
        error,
        runningSchedule,
        runningReminders,
        completingUser,
    } = scheduleStore;

    const {
        updatingTrainingStandards,
        trainingStandardsError,
        trainingRRuleError,
        updatingTrainingRRule,
        updatingTrainingScheduleParams,
        trainingScheduleParamsError,
        updatingTrainingUserLists,
        trainingUserListsError,
    } = superStore;

    const handleLoad = () => {
        const normalizedTrainingId = String(trainingID || '').trim();
        if (!normalizedTrainingId) return;

        if (String(routeTrainingId || '') === normalizedTrainingId) {
            scheduleStore.loadSchedule(normalizedTrainingId);
        } else {
            navigate(`/trainings/${normalizedTrainingId}`);
        }
    };

    const handleChange = (e) => {
        setTrainingID(e.target.value);
    };

    const runSchedule = () => {
        scheduleStore.runSchedule(trainingID);
    };
    const runReminders = () => {
        scheduleStore.runReminders(trainingID);
    };

    useEffect(() => {
        if (!routeTrainingId) return;
        setTrainingID(routeTrainingId);
        scheduleStore.loadSchedule(routeTrainingId);
    }, [routeTrainingId, scheduleStore]);

    useEffect(() => {
        if (schedule) {
            setStandards(schedule?.standards || []);
        }
        if (schedule?.rrule) {
            setRRule(schedule.rrule);
        }
    }, [schedule]);

    useEffect(() => {
        setEditingSchedule(false);
    }, [trainingID]);

    const handleSaveStandards = () => {
        superStore.saveStandardsForTraining(trainingID, { standards });
    };

    const handleSaveRRule = () => {
        superStore.saveRRuleForTraining(trainingID, { rrule });
    };

    const handleChangeStandards = (event) => {
        if (standards.includes(event.target.value)) {
            setStandards(
                standards.filter((standard) => standard !== event.target.value)
            );
        } else setStandards([...standards, event.target.value]);
    };

    const summary = schedule?.summary;

    const openScheduleEdit = () => {
        const f = (summary?.frequency || 'W1').toString().toUpperCase();
        const iv = f.charAt(0);
        const n = parseInt(f.slice(1), 10);
        setEditInterval(['W', 'B', 'M'].includes(iv) ? iv : 'W');
        setEditSubjectsPer(Number.isFinite(n) && n > 0 ? n : 1);
        setEditWeekday(
            summary?.weekday != null ? Number(summary.weekday) : 1
        );
        setEditHour(
            summary?.timeOfDay != null ? Number(summary.timeOfDay) : 9
        );
        setEditingSchedule(true);
    };

    const handleSaveScheduleParams = async () => {
        const frequency = `${editInterval}${editSubjectsPer}`;
        const result = await superStore.saveScheduleParamsForTraining(
            trainingID,
            {
                frequency,
                weekday: editWeekday,
                timeOfDay: editHour,
            }
        );
        if (
            result !== undefined &&
            !superStore.trainingScheduleParamsError
        ) {
            setEditingSchedule(false);
            commonStore.showMessage(
                'Schedule updated. Unsent events were rescheduled.'
            );
            await scheduleStore.loadSchedule(trainingID);
        }
    };

    const openListsDialog = () => {
        const sel = {};
        (schedule?.companyLists || []).forEach((l) => {
            if (l && l.id != null) {
                sel[l.id] = (schedule.attachedListIds || []).includes(l.id);
            }
        });
        setListsSelection(sel);
        setListsDialogOpen(true);
    };

    const handleToggleList = (listId) => {
        setListsSelection((prev) => ({
            ...prev,
            [listId]: !prev[listId],
        }));
    };

    const handleSaveLists = async () => {
        const listIds = Object.keys(listsSelection)
            .filter((id) => listsSelection[id])
            .map((id) => Number(id));
        const result = await superStore.saveUserListsForTraining(trainingID, {
            listIds,
        });
        if (result !== undefined && !superStore.trainingUserListsError) {
            setListsDialogOpen(false);
            commonStore.showMessage(
                result.unchanged
                    ? 'User lists were unchanged.'
                    : 'User lists updated. A FixTrainingTargetMetrics service task was created (see summary).'
            );
            await scheduleStore.loadSchedule(trainingID);
        }
    };

    const handleCompleteUser = async () => {
        const email = completeEmail.trim().toLowerCase();
        if (!email) return;

        const result = await scheduleStore.completeUser(trainingID, email);
        if (result) {
            commonStore.showMessage(
                `Completed ${result.subjectsCompleted} subjects for ${result.email}. ` +
                    `Created ${result.viewedEventsCreated} viewed and ${result.correctEventsCreated} correct events.`
            );
        }
    };

    return (
        <Grid
            container
            spacing={4}
            style={schedule ? { paddingRight: 420 } : undefined}
        >
            <Grid item xs={12}>
                <Errormessage error={error} errorType={'error'} />
            </Grid>
            <Grid item xs={4}>
                <TextField
                    value={trainingID}
                    fullWidth
                    type={'number'}
                    placeholder={'Training ID'}
                    onChange={handleChange}
                ></TextField>
            </Grid>
            <Grid item xs={2}>
                {loadingSchedule ? (
                    <CircularProgress />
                ) : (
                    <Button
                        variant={'contained'}
                        color={'primary'}
                        onClick={handleLoad}
                    >
                        Load
                    </Button>
                )}
            </Grid>

            {schedule && String(schedule.trainingId) === String(trainingID) && (
                <>
                    <Grid item xs={12}>
                        <Paper style={{ padding: 16 }}>
                            <Typography variant="h6">
                                Complete by email
                            </Typography>
                            <Box
                                mt={2}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 12,
                                }}
                            >
                                <TextField
                                    value={completeEmail}
                                    type="email"
                                    label="User email"
                                    placeholder="user@example.com"
                                    onChange={(event) =>
                                        setCompleteEmail(event.target.value)
                                    }
                                    style={{ minWidth: 360 }}
                                />
                                {completingUser ? (
                                    <CircularProgress size={24} />
                                ) : (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        disabled={!completeEmail.trim()}
                                        onClick={handleCompleteUser}
                                    >
                                        Complete
                                    </Button>
                                )}
                            </Box>
                        </Paper>
                    </Grid>
                    <Box
                        style={{
                            position: 'fixed',
                            right: 24,
                            top: 88,
                            width: 480,
                            maxHeight: 'calc(100vh - 110px)',
                            overflowY: 'auto',
                            zIndex: 9,
                        }}
                    >
                        <Paper style={{ padding: 16 }}>
                            <Typography variant="h6" gutterBottom>
                                Training Summary
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Same source fields used during training
                                creation.
                            </Typography>
                            <Box mt={2} style={{ display: 'grid', rowGap: 10 }}>
                                <Typography variant="body2">
                                    <strong style={{ fontWeight: 700 }}>
                                        Name:
                                    </strong>{' '}
                                    {summary?.name || '—'}
                                </Typography>
                                <Typography variant="body2">
                                    <strong style={{ fontWeight: 700 }}>
                                        Id / Version:
                                    </strong>{' '}
                                    {summary?.id || '—'} /{' '}
                                    {summary?.version || '—'}
                                </Typography>
                                <Typography variant="body2">
                                    <strong style={{ fontWeight: 700 }}>
                                        Company ID:
                                    </strong>{' '}
                                    {summary?.companyId != null ? (
                                        <Link to={`/companies/${summary.companyId}`}>
                                            {summary.companyId}
                                        </Link>
                                    ) : (
                                        '—'
                                    )}
                                </Typography>
                                <Typography variant="body2">
                                    <strong style={{ fontWeight: 700 }}>
                                        Status:
                                    </strong>{' '}
                                    {Number(summary?.active) === 1
                                        ? 'Active'
                                        : 'Inactive'}
                                </Typography>
                                <Typography variant="body2">
                                    <strong style={{ fontWeight: 700 }}>
                                        Delivery:
                                    </strong>{' '}
                                    {formatScheduleType(summary?.scheduleType)}
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <strong style={{ fontWeight: 700 }}>
                                        Schedule:
                                    </strong>{' '}
                                    {!editingSchedule ? (
                                        <>
                                            {summary?.frequency
                                                ? formatFrequency(
                                                      summary.frequency
                                                  )
                                                : 'Not configured'}
                                            {summary?.weekday != null
                                                ? `, ${formatWeekday(
                                                      Number(summary.weekday)
                                                  )}`
                                                : ''}
                                            {summary?.timeOfDay != null &&
                                            summary?.timeOfDay !== ''
                                                ? `, at ${summary.timeOfDay}`
                                                : ''}
                                            {Number(summary?.scheduleType) ===
                                                0 &&
                                                summary?.frequency && (
                                                    <IconButton
                                                        size="small"
                                                        aria-label="Edit schedule"
                                                        onClick={
                                                            openScheduleEdit
                                                        }
                                                        disabled={updatingTrainingScheduleParams.includes(
                                                            trainingID
                                                        )}
                                                    >
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                )}
                                        </>
                                    ) : (
                                        <Box
                                            mt={1}
                                            style={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: 8,
                                                alignItems: 'center',
                                            }}
                                        >
                                            {schedule?.rrule ? (
                                                <Typography
                                                    variant="caption"
                                                    color="error"
                                                    style={{
                                                        width: '100%',
                                                    }}
                                                >
                                                    Saving removes the custom
                                                    RRule and uses this simple
                                                    schedule only.
                                                </Typography>
                                            ) : null}
                                            <TextField
                                                label="Subjects"
                                                type="number"
                                                size="small"
                                                style={{ width: 88 }}
                                                value={editSubjectsPer}
                                                onChange={(e) =>
                                                    setEditSubjectsPer(
                                                        Math.min(
                                                            20,
                                                            Math.max(
                                                                1,
                                                                Number(
                                                                    e.target
                                                                        .value
                                                                ) || 1
                                                            )
                                                        )
                                                    )
                                                }
                                                inputProps={{
                                                    min: 1,
                                                    max: 20,
                                                }}
                                            />
                                            <TextField
                                                select
                                                label="Interval"
                                                size="small"
                                                style={{ minWidth: 120 }}
                                                value={editInterval}
                                                onChange={(e) =>
                                                    setEditInterval(
                                                        e.target.value
                                                    )
                                                }
                                            >
                                                {SCHEDULE_INTERVALS.map(
                                                    (opt) => (
                                                        <MenuItem
                                                            key={opt.value}
                                                            value={opt.value}
                                                        >
                                                            {opt.label}
                                                        </MenuItem>
                                                    )
                                                )}
                                            </TextField>
                                            <TextField
                                                select
                                                label="Weekday"
                                                size="small"
                                                style={{ minWidth: 128 }}
                                                value={editWeekday}
                                                onChange={(e) =>
                                                    setEditWeekday(
                                                        Number(e.target.value)
                                                    )
                                                }
                                            >
                                                {[1, 2, 3, 4, 5, 6, 7].map(
                                                    (d) => (
                                                        <MenuItem
                                                            key={d}
                                                            value={d}
                                                        >
                                                            {formatWeekday(d)}
                                                        </MenuItem>
                                                    )
                                                )}
                                            </TextField>
                                            <TextField
                                                select
                                                label="Hour (UTC)"
                                                size="small"
                                                style={{ minWidth: 112 }}
                                                value={editHour}
                                                onChange={(e) =>
                                                    setEditHour(
                                                        Number(e.target.value)
                                                    )
                                                }
                                            >
                                                {HOURS_0_23.map((h) => (
                                                    <MenuItem key={h} value={h}>
                                                        {h}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                            <ErrorMessage
                                                errorType="error"
                                                error={
                                                    trainingScheduleParamsError
                                                }
                                            />
                                            {updatingTrainingScheduleParams.includes(
                                                trainingID
                                            ) ? (
                                                <CircularProgress size={24} />
                                            ) : (
                                                <>
                                                    <Button
                                                        size="small"
                                                        color="primary"
                                                        variant="contained"
                                                        onClick={
                                                            handleSaveScheduleParams
                                                        }
                                                    >
                                                        Save
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        onClick={() =>
                                                            setEditingSchedule(
                                                                false
                                                            )
                                                        }
                                                    >
                                                        Cancel
                                                    </Button>
                                                </>
                                            )}
                                        </Box>
                                    )}
                                </Typography>
                                <Typography variant="body2">
                                    <strong style={{ fontWeight: 700 }}>
                                        Custom RRule:
                                    </strong>{' '}
                                    {schedule?.rrule || '—'}
                                </Typography>
                                <Typography variant="body2">
                                    <strong style={{ fontWeight: 700 }}>
                                        Due date:
                                    </strong>{' '}
                                    {summary?.dueDate
                                        ? moment(summary.dueDate).format('lll')
                                        : '—'}
                                </Typography>
                                <Typography variant="body2">
                                    <strong style={{ fontWeight: 700 }}>
                                        Template:
                                    </strong>{' '}
                                    {summary?.templateId || '—'}
                                </Typography>
                                <Typography variant="body2">
                                    <strong style={{ fontWeight: 700 }}>
                                        Lists ({summary?.userLists?.length || 0}
                                        ):
                                    </strong>{' '}
                                    {(summary?.userLists || [])
                                        .map((x) => x.name || x.id)
                                        .join(', ') || '—'}
                                    <Box mt={0.5}>
                                        <Button
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                            disabled={!trainingID}
                                            onClick={openListsDialog}
                                        >
                                            Edit lists
                                        </Button>
                                    </Box>
                                    <ErrorMessage
                                        errorType="error"
                                        error={trainingUserListsError}
                                    />
                                </Typography>
                                <Typography variant="body2">
                                    <strong style={{ fontWeight: 700 }}>
                                        Subjects (
                                        {summary?.subjects?.length || 0}):
                                    </strong>{' '}
                                    {(summary?.subjects || [])
                                        .map((x) => x.name || x.subject_id)
                                        .join(', ') || '—'}
                                </Typography>
                                <Typography variant="body2">
                                    <strong style={{ fontWeight: 700 }}>
                                        Reminders:
                                    </strong>{' '}
                                    {summary?.remindersEnabled
                                        ? 'Enabled'
                                        : 'Disabled'}
                                </Typography>
                                <Typography variant="body2">
                                    <strong style={{ fontWeight: 700 }}>
                                        Reminder settings:
                                    </strong>{' '}
                                    {summary?.remindersSettings
                                        ? `Channel: ${decodeReminderChannel(summary.remindersSettings.channel)}`
                                        : '—'}
                                </Typography>
                                <Typography variant="body2">
                                    <strong style={{ fontWeight: 700 }}>
                                        Intro message:
                                    </strong>{' '}
                                    {summary?.introMessageEnabled
                                        ? 'Enabled'
                                        : 'Disabled'}
                                </Typography>
                                <Typography variant="body2">
                                    <strong style={{ fontWeight: 700 }}>
                                        Intro subject:
                                    </strong>{' '}
                                    {summary?.introSubject || '—'}
                                </Typography>
                                <Typography variant="body2">
                                    <strong style={{ fontWeight: 700 }}>
                                        Intro text:
                                    </strong>{' '}
                                    {summary?.introMessage
                                        ? stripHtml(summary.introMessage)
                                        : '—'}
                                </Typography>
                                <Typography variant="body2">
                                    <strong style={{ fontWeight: 700 }}>
                                        Created / Updated:
                                    </strong>{' '}
                                    {summary?.created
                                        ? moment(summary.created).format('lll')
                                        : '—'}{' '}
                                    /{' '}
                                    {summary?.updated
                                        ? moment(summary.updated).format('lll')
                                        : '—'}
                                </Typography>
                                <Typography variant="subtitle2" style={{ marginTop: 12 }}>
                                    Service tasks (definition.arguments.trainingId)
                                </Typography>
                                {(schedule?.relatedServiceTasks || []).length ===
                                0 ? (
                                    <Typography variant="body2" color="textSecondary">
                                        None
                                    </Typography>
                                ) : (
                                    <Box
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 4,
                                            maxHeight: 200,
                                            overflowY: 'auto',
                                        }}
                                    >
                                        {(schedule?.relatedServiceTasks || []).map(
                                            (t) => (
                                                <Typography
                                                    key={t.id}
                                                    variant="body2"
                                                >
                                                    <Link
                                                        to={`/serviceTasks/${t.id}/edit`}
                                                    >
                                                        #{t.id} {t.name}
                                                    </Link>
                                                    {' — '}
                                                    status {t.status}
                                                    {t.created
                                                        ? ` — ${moment(
                                                              t.created
                                                          ).format('lll')}`
                                                        : ''}
                                                </Typography>
                                            )
                                        )}
                                    </Box>
                                )}
                            </Box>
                        </Paper>
                    </Box>
                    <Dialog
                        open={listsDialogOpen}
                        onClose={() => setListsDialogOpen(false)}
                        maxWidth="sm"
                        fullWidth
                    >
                        <DialogTitle>Edit training user lists</DialogTitle>
                        <DialogContent dividers style={{ maxHeight: 480 }}>
                            {(schedule?.companyLists || []).length === 0 ? (
                                <Typography color="textSecondary">
                                    No lists for this company.
                                </Typography>
                            ) : (
                                (schedule?.companyLists || []).map((l) => (
                                    <FormControlLabel
                                        key={l.id}
                                        control={
                                            <Checkbox
                                                color="primary"
                                                checked={!!listsSelection[l.id]}
                                                onChange={() =>
                                                    handleToggleList(l.id)
                                                }
                                            />
                                        }
                                        label={`${l.name} (id ${l.id})`}
                                    />
                                ))
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setListsDialogOpen(false)}>
                                Cancel
                            </Button>
                            {updatingTrainingUserLists.includes(trainingID) ? (
                                <CircularProgress size={24} />
                            ) : (
                                <Button
                                    color="primary"
                                    variant="contained"
                                    onClick={handleSaveLists}
                                >
                                    Save
                                </Button>
                            )}
                        </DialogActions>
                    </Dialog>
                    <Grid item xs={12}>
                        <Typography variant={'h5'}>
                            {schedule.name} - version {schedule.version}
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant={'h6'}>Standards:</Typography>
                        <br />
                        <ErrorMessage
                            errorType="error"
                            error={trainingStandardsError}
                        />
                        <Box mt={4}>
                            Standards:
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        value={'SOC2'}
                                        checked={standards.includes('SOC2')}
                                        onChange={handleChangeStandards}
                                        name="SOC2"
                                        color="primary"
                                    />
                                }
                                label="SOC2"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        value={'ISO27001'}
                                        checked={standards.includes('ISO27001')}
                                        onChange={handleChangeStandards}
                                        name="ISO27001"
                                        color="primary"
                                    />
                                }
                                label="ISO27001"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        value={'HIPAA'}
                                        checked={standards.includes('HIPAA')}
                                        onChange={handleChangeStandards}
                                        name="HIPAA"
                                        color="primary"
                                    />
                                }
                                label="HIPAA"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        value={'GDPR'}
                                        checked={standards.includes('GDPR')}
                                        onChange={handleChangeStandards}
                                        name="GDPR"
                                        color="primary"
                                    />
                                }
                                label="GDPR"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        value={'CCPA'}
                                        checked={standards.includes('CCPA')}
                                        onChange={handleChangeStandards}
                                        name="CCPA"
                                        color="primary"
                                    />
                                }
                                label="CCPA"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        value={'PCI'}
                                        checked={standards.includes('PCI')}
                                        onChange={handleChangeStandards}
                                        name="PCI"
                                        color="primary"
                                    />
                                }
                                label="PCI"
                            />
                            {updatingTrainingStandards.includes(trainingID) ? (
                                <CircularProgress />
                            ) : (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    disabled={_.isEqual(
                                        standards,
                                        schedule?.standards || []
                                    )}
                                    onClick={handleSaveStandards}
                                >
                                    Save
                                </Button>
                            )}
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <ErrorMessage
                            errorType="error"
                            error={trainingRRuleError}
                        />
                        <Box mt={4}>
                            Custom RRule:
                            <TextField
                                value={rrule}
                                onChange={(e) => {
                                    setRRule(e.target.value || '');
                                }}
                                name="rrule"
                            />
                            {updatingTrainingRRule.includes(trainingID) ? (
                                <CircularProgress />
                            ) : (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    disabled={_.isEqual(
                                        rrule,
                                        schedule?.rrule || ''
                                    )}
                                    onClick={handleSaveRRule}
                                >
                                    Save
                                </Button>
                            )}
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant={'h6'}>Schedule:</Typography>
                        <br />
                        {runningSchedule ? (
                            <CircularProgress />
                        ) : (
                            <Button
                                color={'primary'}
                                variant={'contained'}
                                onClick={runSchedule}
                            >
                                Run &quot;Shedule&quot; sending
                            </Button>
                        )}
                        {runningReminders ? (
                            <CircularProgress />
                        ) : (
                            <Button
                                color={'primary'}
                                variant={'contained'}
                                onClick={runReminders}
                            >
                                Run &quot;Reminders&quot; sending
                            </Button>
                        )}
                    </Grid>
                    <Grid item xs={12}>
                        <table>
                            <thead>
                                <tr>
                                    <td style={{ padding: '20px' }}>Id:</td>
                                    <td style={{ padding: '20px' }}>Type:</td>
                                    <td style={{ padding: '20px' }}>
                                        Subjects:
                                    </td>
                                    <td style={{ padding: '20px' }}>Status:</td>
                                    <td style={{ padding: '20px' }}>Send:</td>
                                    <td style={{ padding: '20px' }}></td>
                                    <td></td>
                                </tr>
                            </thead>
                            <tbody>
                                {schedule.events.map((e, i) => (
                                    <Row
                                        key={e.id}
                                        data={e}
                                        index={i}
                                        trainingID={trainingID}
                                        subjects={e.subjects}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </Grid>
                </>
            )}
        </Grid>
    );
}

export default observer(Schedule);
